import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ethers } from 'ethers';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useWriteContract } from '../hooks/useWriteContract';
import { useCountdown, formatCountdown } from '../hooks/useCountdown';
import { pushToast } from '../lib/toast';
import { ADDRESSES } from '../lib/chain';
import {
  useCurrentPeriod,
  usePeriodRaisedWei,
  useAvgPeriodRaisedWei,
  useTokensCreatedPerPeriod,
  useRemainingPeriodTime,
  useContributorsLength,
  useAccountClaimableTokens,
  useMinContribution,
  useAccountCurrentPeriodContribution,
} from '../hooks/useCrowdsale';

export default function Auction() {
  const account = useAccount();
  const [amount, setAmount] = useState('0.05');
  const [donateAmount, setDonateAmount] = useState('0.01');
  const { write: writeContribute, status: contribStatus } = useWriteContract();
  const { write: writeClaim, status: claimStatus } = useWriteContract();
  const { write: writeDonate, status: donateStatus } = useWriteContract();

  const period      = useCurrentPeriod();
  const raised      = usePeriodRaisedWei(0);
  const avgRaised   = useAvgPeriodRaisedWei();
  const tokensPer   = useTokensCreatedPerPeriod();
  const ttnp        = useRemainingPeriodTime();
  const contribLen  = useContributorsLength(0);
  const minContrib  = useMinContribution();
  const claimable   = useAccountClaimableTokens(account);
  const myContrib   = useAccountCurrentPeriodContribution(account);

  // bigint 0n is falsy — explicit undefined check keeps zero values.
  // Note: ETH values are wei-scaled (÷1e18); tokensCreatedPerPeriod is stored
  // RAW (not wei-METH) — the contract scales internally when minting.
  const periodNum         = period.data     !== undefined ? Number(period.data)         : null;
  const raisedEth         = raised.data     !== undefined ? weiToEth(raised.data)       : 0;
  const avgRaisedEth      = avgRaised.data  !== undefined ? weiToEth(avgRaised.data)    : 0;
  const tokensPerNum      = tokensPer.data  !== undefined ? Number(tokensPer.data)      : 287;
  const minContribEth     = minContrib.data !== undefined ? weiToEth(minContrib.data)   : 0.001;
  const claimableMeth     = claimable.data  !== undefined ? weiToEth(claimable.data)    : 0;
  const myContribEth      = myContrib.data  !== undefined ? weiToEth(myContrib.data)    : 0;
  const contribCount      = contribLen.data !== undefined ? Number(contribLen.data)     : 0;
  // Live-ticking countdown — re-syncs every time the source refetches.
  const countdownSec      = useCountdown(ttnp.data !== undefined ? Number(ttnp.data) : undefined);
  const ttnpStr           = formatCountdown(countdownSec);

  // contribute estimator
  const v = parseFloat(amount || '0') || 0;
  const total = raisedEth + v;
  const sharePct = total > 0 ? (v / total) * 100 : 0;
  // Contributors only get 70% of the per-period emission (whitepaper §7:
  // 70 contributors / 20 operations / 10 developers). The remaining 30% is
  // minted to the operationStash + dev stashes at claim time.
  const CONTRIBUTOR_PCT = 70;
  const estClaimable = total > 0 ? (v / total) * tokensPerNum * (CONTRIBUTOR_PCT / 100) : 0;

  // ETH amount in wei, null if parsing fails
  let contribWei: bigint | null = null;
  try { contribWei = ethers.parseEther(amount || '0'); } catch { contribWei = null; }
  let donateWei: bigint | null = null;
  try { donateWei = ethers.parseEther(donateAmount || '0'); } catch { donateWei = null; }

  const contribSubmitting = contribStatus === 'submitting' || contribStatus === 'pending';
  const claimSubmitting   = claimStatus   === 'submitting' || claimStatus   === 'pending';
  const donateSubmitting  = donateStatus  === 'submitting' || donateStatus  === 'pending';
  const canContribute = !!account && contribWei !== null && contribWei >= (minContrib.data ?? 0n) && !contribSubmitting;
  const canDonate     = !!account && donateWei  !== null && donateWei > 0n && !donateSubmitting;

  async function onContribute() {
    if (!canContribute || contribWei === null) return;
    await writeContribute({
      contract: 'crowdsale',
      fn: 'contributeCurrentPeriod',
      args: [],
      options: {
        value: contribWei,
        pendingTitle: `Contributing ${weiToEth(contribWei)} ETH…`,
        successTitle: `Contributed ${weiToEth(contribWei)} ETH`,
        invalidate: [['metha', 'crowdsale']],
      },
    });
  }

  async function onClaimAll() {
    if (!account || claimableMeth === 0 || claimSubmitting) return;
    // claimAll iterates the user's unclaimed periods.
    await writeClaim({
      contract: 'crowdsale',
      fn: 'claimAll',
      args: [],
      options: {
        pendingTitle: `Claiming ${fmt.meth(claimableMeth)} METH…`,
        successTitle: `Claimed ${fmt.meth(claimableMeth)} METH`,
        invalidate: [['metha', 'crowdsale'], ['metha', 'token']],
      },
    });
  }

  async function onDonate() {
    if (!canDonate || donateWei === null) return;
    await writeDonate({
      contract: 'crowdsale',
      fn: 'donateToVault',
      args: [],
      options: {
        value: donateWei,
        pendingTitle: `Donating ${weiToEth(donateWei)} ETH…`,
        successTitle: `Donated ${weiToEth(donateWei)} ETH`,
        invalidate: [['metha', 'crowdsale']],
      },
    });
  }

  return (
    <Page ctxLabel="auction" title="Metha · Auction" docsPath="how-it-works/auctions" docsLabel="How auctions work">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Auction' }]} />

      <div className="ph">
        <div>
          <h1>Auction <span className="acc">/ period {periodNum ?? '…'}</span></h1>
          <p className="lede">
            Perpetual proportional auction. Anyone contributes ETH; tokens are
            distributed proportionally at period close. <b>Emission only mints
            in periods that received at least one contribution — empty periods
            produce 0 METH.</b> Non-gameable: price discovery happens after
            the window closes.
          </p>
        </div>
        <div className="meta">
          <span>PERIOD <b>{periodNum ?? '…'}</b></span>
          <span title="Emission only mints in periods that received at least one contribution — empty periods produce 0 METH.">
            EMISSION <b>{tokensPerNum} METH</b><span className="dim" style={{ fontSize: 10, marginLeft: 4 }}>/ contributed period</span>
          </span>
          <span>CLOSES IN <b className="mono" style={{ color: countdownSec !== null && countdownSec < 60 ? 'var(--amber)' : 'var(--ink)' }}>{ttnpStr}</b></span>
        </div>
      </div>

      <div className="row r-2u1 mb-14">
        <div>
          <div className="panel mb-14">
            <div className="p-head">
              <h3>Period {periodNum ?? '…'} · live</h3>
              <span className="src"><code>crowdsale.getWeiRaisedPerPeriod(0)</code></span>
            </div>
            <div className="row r-2" style={{ padding: 18, gap: 18 }}>
              <div className="big-stat" style={{ padding: 0, borderLeft: 0 }}>
                {/* Countdown sits right above the ETH RAISED number — same
                    visual block so the user has time + money at a glance. */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                  <span className="mono" style={{ fontSize: 9, letterSpacing: 1.4, color: 'var(--muted)', textTransform: 'uppercase' }}>
                    closes in
                  </span>
                  <span
                    className="mono"
                    style={{
                      fontSize: 24,
                      fontWeight: 600,
                      letterSpacing: 1,
                      fontVariantNumeric: 'tabular-nums',
                      color: countdownSec !== null && countdownSec < 60 ? 'var(--amber)' : 'var(--ink)',
                      lineHeight: 1,
                    }}
                  >
                    {ttnpStr}
                  </span>
                </div>
                <div className="l">ETH RAISED THIS PERIOD</div>
                <div className="v">{raisedEth.toFixed(4)}<span className="u">ETH</span></div>
                <div className="d">{contribCount} contributor{contribCount === 1 ? '' : 's'} · refetches every 12s</div>

                {/* Top-5 contributors of the live period, slotted into the
                    empty space below ETH RAISED. Fed by the same query the
                    petri uses (TanStack Query dedupes the RPC calls). */}
                <TopContributorsPanel
                  periodId={0}
                  contribCount={contribCount}
                  weiRaised={raised.data ?? 0n}
                  tokensPerPeriod={tokensPerNum}
                />
              </div>
              <PeriodBookPetri
                weiRaised={raised.data ?? 0n}
                contribCount={contribCount}
                periodId={0}
                tokensPerPeriod={tokensPerNum}
              />
            </div>

            <div className="kpi-row" style={{ border: 0, borderTop: '1px solid var(--line)', borderRadius: 0, gridTemplateColumns: 'repeat(4,1fr)' }}>
              <Kpi
                l="EMISSION / CONTRIBUTED PERIOD"
                v={<>{tokensPerNum}<span className="u">METH</span></>}
                d="empty periods mint 0"
              />
              <Kpi l="YOUR CONTRIB · NOW" v={
                account
                  ? <>{myContribEth.toFixed(4)}<span className="u">ETH</span></>
                  : <span style={{ fontSize: 14, color: 'var(--dim)' }}>—</span>
              } d={account ? 'in current period' : 'connect wallet'} />
              <Kpi l="AVG / PERIOD" v={<>{avgRaisedEth.toFixed(4)}<span className="u">ETH</span></>} d="across closed periods" />
              <Kpi l="MIN CONTRIBUTION" v={<>{minContribEth}<span className="u">ETH</span></>} d="setMinContribution" />
            </div>
          </div>

          <PeriodBook periodId={0} count={contribCount} myAddress={account} />

          <ScheduleContributionsCard
            account={account}
            currentPeriod={periodNum}
            minContribEth={minContribEth}
          />

          <PeriodHistory currentPeriod={periodNum} />
        </div>

        <aside>
          <div className="panel mb-14">
            <div className="p-head"><h3>Contribute · period {periodNum ?? '…'}</h3><span className="src"><code>contributeCurrentPeriod()</code></span></div>
            <div className="p-body">
              <span className="field-l">Amount</span>
              <div className="input lg">
                <input type="text" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <span className="u">ETH</span>
              </div>
              <div className="estim mt-14"><span>EST. METH (post-window)</span><b>~ {estClaimable.toFixed(2)} <span className="dim mono" style={{ fontSize: 9, marginLeft: 4 }}>(70% split)</span></b></div>
              <div className="estim"><span>SHARE OF PERIOD</span><b>{sharePct.toFixed(1)}%</b></div>
              <div className="estim"><span>PERIOD CLOSES</span><b>{ttnpStr}</b></div>
              <div className="estim"><span>MIN CONTRIBUTION</span><b>{minContribEth} ETH</b></div>
              <button
                className={`btn btn-prim btn-block btn-lg mt-14${canContribute ? '' : ' btn-locked'}`}
                disabled={!canContribute}
                onClick={onContribute}
              >
                {contribSubmitting ? 'Submitting…' :
                  !account ? 'Connect wallet to contribute' :
                  contribWei === null ? 'Invalid amount' :
                  contribWei < (minContrib.data ?? 0n) ? `Below minimum (${minContribEth} ETH)` :
                  `Contribute ${v.toFixed(4)} ETH`}
              </button>
              <p className="help mt-14">
                You receive an entitlement to claim a proportional share of {tokensPerNum * 0.7} METH
                (70% of the {tokensPerNum} per-period emission — the remaining 20% goes to operations,
                10% to developers). METH cannot be claimed until the period closes.
              </p>
            </div>
          </div>

          <div className="panel mb-14">
            <div className="p-head"><h3>Your claim queue</h3><span className="src"><code>getAccountClaimableTokens()</code></span></div>
            <div className="p-body">
              <div className="estim"><span>UNCLAIMED METH</span>
                <b>
                  {account
                    ? fmt.meth(claimableMeth)
                    : <span className="dim">connect wallet</span>}
                </b>
              </div>
              <div className="estim"><span>FROM PAST PERIODS</span>
                <b>{account ? (claimable.isLoading ? '…' : 'aggregated') : '—'}</b>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button
                  className={`btn btn-prim${!account || claimableMeth === 0 || claimSubmitting ? ' btn-locked' : ''}`}
                  style={{ flex: 1 }}
                  disabled={!account || claimableMeth === 0 || claimSubmitting}
                  onClick={onClaimAll}
                >
                  {claimSubmitting ? 'Claiming…' :
                    account
                      ? claimableMeth > 0
                        ? `Claim all (${fmt.meth(claimableMeth)} METH)`
                        : 'Nothing to claim'
                      : 'Connect wallet'}
                </button>
                <AddMethToWalletButton />
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="p-head"><h3>Donate to vault</h3><span className="src"><code>donateToVault()</code></span></div>
            <div className="p-body">
              <p className="help" style={{ marginTop: 0 }}>
                Send ETH to the treasury without minting METH. No claim, no expectation
                of return — pure topping-up of the research stash.
              </p>
              <span className="field-l">Amount</span>
              <div className="input">
                <input type="text" value={donateAmount} onChange={(e) => setDonateAmount(e.target.value)} />
                <span className="u">ETH</span>
              </div>
              <button
                className={`btn btn-ghost btn-block mt-14${canDonate ? '' : ' btn-locked'}`}
                disabled={!canDonate}
                onClick={onDonate}
              >
                {donateSubmitting ? 'Donating…' : !account ? 'Connect wallet to donate' : 'Donate ETH'}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </Page>
  );
}

/* -------------------------------------------------------------- *
 *   Period book — iterates getContributorsPeriodIndex(periodId, i)
 * -------------------------------------------------------------- */

function PeriodBook({
  periodId,
  count,
  myAddress,
}: { periodId: number; count: number; myAddress: string | null }) {
  const contracts = useReadContracts();

  const queries = useQueries({
    queries: Array.from({ length: count }, (_, i) => ({
      queryKey: ['metha', 'crowdsale', 'getContributorsPeriodIndex', periodId, i] as const,
      queryFn: async () => {
        const tuple = await contracts.crowdsale.getContributorsPeriodIndex(BigInt(periodId), BigInt(i));
        return tuple as [string, bigint, bigint, bigint, boolean];
      },
      staleTime: 15_000,
    })),
  });

  const rows = queries
    .map((q) => q.data)
    .filter((d): d is [string, bigint, bigint, bigint, boolean] => !!d);

  const totalWei = rows.reduce((s, r) => s + r[1], 0n);

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Period book · contributions in #{periodId === 0 ? 'live' : periodId}</h3>
        <span className="src"><code>getContributorsPeriodIndex(0, i) × {count}</code></span>
      </div>
      <div className="bid-row" style={{ background: 'var(--panel-2)', fontSize: 10, letterSpacing: 1, color: 'var(--muted)', textTransform: 'uppercase' }}>
        <span>WALLET</span><span>AMOUNT</span><span>SHARE</span><span>CLAIMED</span>
      </div>
      {count === 0 && (
        <div style={{ padding: 18, color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
          No contributions yet in this period — be the first to inoculate.
        </div>
      )}
      {rows.map((r, i) => {
        // Contract return order: (addr, tokenBalance, weiContribution, date, claimed).
        // tokenBalance is 0 until period closes → we need index 2 (wei).
        const addr = r[0];
        const wei = r[2];
        const claimed = r[4];
        const sharePct = totalWei > 0n ? (Number(wei) / Number(totalWei)) * 100 : 0;
        const isYou = myAddress?.toLowerCase() === addr.toLowerCase();
        return (
          <div key={i} className="bid-row">
            <span className="a">
              {short(addr)}
              {isYou && <Pill tone="ctx">YOU</Pill>}
            </span>
            <span className="v">{weiToEth(wei).toFixed(4)} ETH</span>
            <span className="v">{sharePct.toFixed(1)}%</span>
            <span className="b">{claimed ? 'claimed' : '— pending'}</span>
          </div>
        );
      })}
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', letterSpacing: 0.5 }}>
        Returns (address, weiContribution, tokenBalance, periodIdx, claimed) · "pending" until period closes.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- *
 *   Period history — bar chart of last N closed periods
 * -------------------------------------------------------------- */

function PeriodHistory({ currentPeriod }: { currentPeriod: number | null }) {
  const contracts = useReadContracts();
  // Show up to 12 most recent CLOSED periods. The current period itself is
  // still live (covered by the "Period {N} · live" card), so we cap at
  // currentPeriod - 1. If currentPeriod is 0 or 1, no closed periods exist
  // yet.
  const lastClosed = Math.max(0, (currentPeriod ?? 0) - 1);
  const N = Math.min(12, lastClosed);
  // Window of period IDs we display (most-recent last).
  const startPid = Math.max(1, lastClosed - N + 1);
  const pids = Array.from({ length: N }, (_, i) => startPid + i);
  const [hover, setHover] = useState<number | null>(null);

  const queries = useQueries({
    queries: pids.map((pid) => ({
      queryKey: ['metha', 'crowdsale', 'getWeiRaisedPerPeriod', pid] as const,
      queryFn: async () => {
        const wei = (await contracts.crowdsale.getWeiRaisedPerPeriod(BigInt(pid))) as bigint;
        return weiToEth(wei);
      },
      staleTime: 60_000,
    })),
  });

  const values = queries.map((q) => q.data ?? 0);
  const max = Math.max(0.0001, ...values);
  const totalEth = values.reduce((s, v) => s + v, 0);

  return (
    <div className="panel">
      <div className="p-head">
        <h3>Period history · {N === 0 ? 'no closed periods yet' : `last ${N} closed periods`}</h3>
        <span className="src">
          {N > 0
            ? `getWeiRaisedPerPeriod(${startPid}..${startPid + N - 1}) · Σ ${totalEth.toFixed(4)} ETH`
            : 'getWeiRaisedPerPeriod(…)'}
        </span>
      </div>
      <div style={{ padding: 18 }}>
        {N === 0 ? (
          <div style={{ color: 'var(--dim)', fontSize: 12, fontFamily: 'var(--mono)' }}>
            Period {currentPeriod ?? 1} is the first — history fills in as it rolls over.
          </div>
        ) : (
          <>
            {/* Floating tooltip with the hovered period's value */}
            <div style={{ height: 22, marginBottom: 6, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink)', textAlign: 'center', letterSpacing: 0.5 }}>
              {hover !== null ? (
                <>
                  <span className="acc">P{pids[hover]}</span>{' · '}
                  <b>{values[hover].toFixed(4)} ETH</b>{' raised'}
                </>
              ) : (
                <span className="dim">hover bars for per-period detail</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${N},1fr)`, gap: 6, alignItems: 'end', height: 120 }}>
              {values.map((v, i) => {
                const hPct = max > 0 ? Math.max(v === 0 ? 4 : 8, (v / max) * 100) : 0;
                const isHover = hover === i;
                return (
                  <div
                    key={i}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                    title={`Period ${pids[i]} · ${v.toFixed(4)} ETH`}
                    style={{
                      background: v === 0 ? 'var(--line)' : isHover ? 'var(--ctx)' : 'var(--ctx-soft)',
                      borderTop: `2px solid ${v === 0 ? 'var(--line-2)' : 'var(--ctx)'}`,
                      height: `${hPct}%`,
                      borderRadius: '2px 2px 0 0',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                    }}
                  />
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${N},1fr)`, gap: 6, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', textAlign: 'center', marginTop: 6, letterSpacing: 0.5 }}>
              {pids.map((pid) => <span key={pid}>P{pid}</span>)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- *
 *   Mini petri visualization
 * -------------------------------------------------------------- */

/**
 * Color palette for petri colonies and matching list rows. Picked to be
 * distinguishable on the dark theme without clashing with the green
 * context accent. The 11th color (gray) is reserved for the synthetic
 * "others" bucket.
 */
const COLONY_COLORS = [
  '#3ddb89', // green (matches var(--ctx))
  '#4ea8ff', // blue
  '#a978ff', // purple
  '#f5b54a', // amber
  '#2dd4bf', // teal
  '#ec4899', // pink
  '#fb7185', // rose
  '#a3e635', // lime
  '#fbbf24', // yellow
  '#fca5a5', // salmon
];
const OTHERS_COLOR = '#525c75'; // var(--muted)-ish slate

export type RankedContributor = {
  addr: string;          // "others" for the aggregated tail
  wei: bigint;
  share: number;         // 0..1
  color: string;
  isOthers: boolean;
};

/**
 * Rank contributors, keep the top 10, fold the rest into a synthetic
 * "others" entry. Returns at most 11 rows. Colors are assigned in rank
 * order from COLONY_COLORS; the "others" entry always gets OTHERS_COLOR.
 */
function rankWithOthers(
  contribs: readonly [string, bigint, bigint, bigint, boolean][],
  totalWei: bigint,
  topN: number = 10,
): RankedContributor[] {
  const sorted = [...contribs]
    .map((c) => ({ addr: c[0], wei: c[2] }))
    .sort((a, b) => (b.wei > a.wei ? 1 : b.wei < a.wei ? -1 : 0));

  const top = sorted.slice(0, topN);
  const tail = sorted.slice(topN);
  const tailWei = tail.reduce((s, c) => s + c.wei, 0n);

  const result: RankedContributor[] = top.map((c, i) => ({
    addr: c.addr,
    wei: c.wei,
    share: totalWei > 0n ? Number(c.wei) / Number(totalWei) : 0,
    color: COLONY_COLORS[i % COLONY_COLORS.length],
    isOthers: false,
  }));

  if (tail.length > 0 && tailWei > 0n) {
    result.push({
      addr: 'others',
      wei: tailWei,
      share: totalWei > 0n ? Number(tailWei) / Number(totalWei) : 0,
      color: OTHERS_COLOR,
      isOthers: true,
    });
  }
  return result;
}

/**
 * Top contributors of a period, top 10 + a single aggregated "others"
 * row when there are more than 10. Reuses the same TanStack query keys
 * as PeriodBookPetri so the RPC calls dedupe. Color dots match the
 * petri colony colors so the user can correlate list ↔ visualization.
 */
function TopContributorsPanel({
  periodId, contribCount, weiRaised, tokensPerPeriod,
}: {
  periodId: number;
  contribCount: number;
  weiRaised: bigint;
  tokensPerPeriod: number;
}) {
  const contracts = useReadContracts();
  const queries = useQueries({
    queries: Array.from({ length: contribCount }, (_, i) => ({
      queryKey: ['metha', 'crowdsale', 'getContributorsPeriodIndex', periodId, i] as const,
      queryFn: async () => (await contracts.crowdsale.getContributorsPeriodIndex(BigInt(periodId), BigInt(i))) as [string, bigint, bigint, bigint, boolean],
      staleTime: 15_000,
    })),
  });
  const contribs = queries.map((q) => q.data).filter((d): d is [string, bigint, bigint, bigint, boolean] => !!d);
  const totalWei = contribs.reduce((s, c) => s + c[2], 0n) || weiRaised || 1n;
  const contributorEmission = tokensPerPeriod * 0.70;
  const ranked = useMemo(() => rankWithOthers(contribs, totalWei, 10), [contribs, totalWei]);

  if (ranked.length === 0) return null;

  return (
    <div style={{ marginTop: 18 }}>
      <div className="mono dim" style={{ fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 }}>
        Top contributors · this period
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ranked.map((c, i) => {
          const sharePct = c.share * 100;
          const methAlloc = contributorEmission * c.share;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 11 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: c.color,
                  flexShrink: 0,
                  boxShadow: c.isOthers ? 'none' : `0 0 0 2px ${c.color}22`,
                }}
                title={c.isOthers ? 'aggregated tail' : `colony #${i + 1}`}
              />
              <span className="dim" style={{ width: 24 }}>
                {c.isOthers ? '+' : `#${i + 1}`}
              </span>
              <span style={{ width: 96, color: 'var(--ink)' }}>
                {c.isOthers ? <em style={{ color: 'var(--dim)', fontStyle: 'normal' }}>others</em> : short(c.addr)}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: 'var(--line)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: `${Math.max(2, sharePct)}%`,
                    height: '100%',
                    background: c.color,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <span style={{ width: 56, textAlign: 'right', color: 'var(--ink)' }}>
                {sharePct.toFixed(1)}%
              </span>
              <span style={{ width: 80, textAlign: 'right', color: 'var(--dim)', fontSize: 10 }}>
                {weiToEth(c.wei).toFixed(4)} ETH
              </span>
              <span
                style={{ width: 80, textAlign: 'right', color: 'var(--green)', fontSize: 10 }}
                title={`Will claim ${methAlloc.toFixed(2)} METH when the period closes (70% × ${sharePct.toFixed(1)}%)`}
              >
                ≈ {methAlloc.toFixed(0)} METH
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Period book visualised as a petri dish (SVG with viewBox so sizes are
 * exact proportions, not arbitrary CSS pixels). Each "colony" is a
 * contributor; colony radius is √share × DISH_RADIUS, so colony AREA is
 * exactly proportional to the contributor's wei share.
 *
 * Edge cases:
 *   - 1 contributor: one colony of radius √1 = 1 × maxR → filling ~70% of
 *     the dish diameter (since we cap maxR = 0.70 × DISH_RADIUS).
 *   - N contributors, equal share: each radius = √(1/N) × maxR. For N=3,
 *     each colony has radius ~0.58 × maxR ≈ ~40% of dish diameter.
 *
 * Hovering a colony shows a styled floating popup (panel-bordered) that
 * follows the cursor with addr / ETH / share / METH allocation.
 */
function PeriodBookPetri({
  periodId, contribCount, weiRaised, tokensPerPeriod,
}: {
  periodId: number;
  contribCount: number;
  weiRaised: bigint;
  tokensPerPeriod: number;
}) {
  const contracts = useReadContracts();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{
    addr: string;
    weiEth: number;
    sharePct: number;
    methAlloc: number;
    color: string;
    isOthers: boolean;
    cursorX: number;
    cursorY: number;
  } | null>(null);

  const queries = useQueries({
    queries: Array.from({ length: contribCount }, (_, i) => ({
      queryKey: ['metha', 'crowdsale', 'getContributorsPeriodIndex', periodId, i] as const,
      queryFn: async () => (await contracts.crowdsale.getContributorsPeriodIndex(BigInt(periodId), BigInt(i))) as [string, bigint, bigint, bigint, boolean],
      staleTime: 15_000,
    })),
  });
  const contribs = queries.map((q) => q.data).filter((d): d is [string, bigint, bigint, bigint, boolean] => !!d);

  // SVG viewBox is 100×100. Dish lives inside [0,100]×[0,100].
  const DISH_CX = 50;
  const DISH_CY = 50;
  const DISH_R  = 48;          // dish glass border radius
  const MARGIN  = 1.5;         // padding between colony edge and glass
  const MAX_R   = DISH_R - MARGIN;
  const MIN_R   = 1.4;         // never invisible

  // Contract return order: (addr, tokenBalance, weiContribution, date, claimed).
  const totalWei = contribs.reduce((s, c) => s + c[2], 0n) || weiRaised || 1n;
  const contributorEmission = tokensPerPeriod * 0.70;

  // Aggregate beyond top 10 into a synthetic "others" colony so the petri
  // doesn't clutter with dozens of dust-size dots. The list panel uses the
  // same reduction so user can correlate dots ↔ rows.
  const ranked = useMemo(() => rankWithOthers(contribs, totalWei, 10), [contribs, totalWei]);

  /**
   * Place N colonies in the dish such that:
   *   - colony area ∝ share (radius ∝ √share)
   *   - no two colonies overlap
   *   - all colonies fit inside the dish minus margin
   *
   * Strategy: arrange around a ring with equal angular spacing. The ring
   * radius R is chosen so the tightest adjacent pair just touches
   *   (distance between centres = 2·R·sin(π/N) = rᵢ + rᵢ₊₁).
   * Then scale everything down if R + max(rᵢ) > MAX_R.
   *
   * N=1 is special: a single colony fills the dish (capped to MAX_R).
   */
  const colonies = (() => {
    const N = ranked.length;
    if (N === 0) return [];
    if (N === 1) {
      return [{ ...ranked[0], x: DISH_CX, y: DISH_CY, r: MAX_R }];
    }

    // Base radii proportional to √share, scaled so the LARGEST is MAX_R.
    // This gives us a generous starting size; the ring-fit step below
    // shrinks the whole arrangement if anything would overlap.
    const sqrtShares = ranked.map((c) => Math.sqrt(c.share));
    const maxSqrt = Math.max(...sqrtShares);
    const base = sqrtShares.map((s) => Math.max(MIN_R, (s / maxSqrt) * MAX_R));

    // Min ring radius for no-overlap. Equal angular spacing → adjacent
    // chord = 2·R·sin(π/N). Take the max over all adjacent pairs (any
    // pair that violates the constraint forces R up).
    const sinPi_N = Math.sin(Math.PI / N);
    let ringR = 0;
    for (let i = 0; i < N; i++) {
      const r1 = base[i];
      const r2 = base[(i + 1) % N];
      const need = (r1 + r2) / (2 * sinPi_N);
      if (need > ringR) ringR = need;
    }

    // Fit-in-dish step: ringR + biggest colony radius ≤ MAX_R.
    const biggest = Math.max(...base);
    let scale = 1;
    if (ringR + biggest > MAX_R) {
      scale = MAX_R / (ringR + biggest);
      ringR = ringR * scale;
    }
    const radii = base.map((r) => r * scale);

    return ranked.map((c, i) => {
      const angle = (i / N) * Math.PI * 2 - Math.PI / 2; // start at 12 o'clock
      return {
        ...c,
        x: DISH_CX + ringR * Math.cos(angle),
        y: DISH_CY + ringR * Math.sin(angle),
        r: radii[i],
      };
    });
  })();

  const showStashes = contribs.length > 0 && totalWei > 0n;
  const opsMeth = tokensPerPeriod * 0.20;
  const devMeth = tokensPerPeriod * 0.10;

  function bindHover(c: typeof colonies[number]) {
    return {
      onMouseEnter: (e: React.MouseEvent) => {
        const container = containerRef.current?.getBoundingClientRect();
        if (!container) return;
        setHover({
          addr: c.addr,
          weiEth: weiToEth(c.wei),
          sharePct: c.share * 100,
          methAlloc: c.share * contributorEmission,
          color: c.color,
          isOthers: c.isOthers,
          cursorX: e.clientX - container.left,
          cursorY: e.clientY - container.top,
        });
      },
      onMouseMove: (e: React.MouseEvent) => {
        const container = containerRef.current?.getBoundingClientRect();
        if (!container) return;
        setHover((prev) => prev && {
          ...prev,
          cursorX: e.clientX - container.left,
          cursorY: e.clientY - container.top,
        });
      },
      onMouseLeave: () => setHover(null),
    };
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {/* 15% smaller dish — wrap the SVG in a centred container that's
          85% of the parent width. The viewBox stays 100×100, so colony
          geometry doesn't change; only the rendered size shrinks. */}
      <div style={{ width: '85%', margin: '0 auto', alignSelf: 'center' }}>
        <svg
          viewBox="0 0 100 100"
          style={{ width: '100%', height: 'auto', aspectRatio: '1 / 1', display: 'block' }}
        >
          <defs>
            <radialGradient id="dishGloss" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.04)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
          </defs>
          {/* Dish glass */}
          <circle cx={DISH_CX} cy={DISH_CY} r={DISH_R} fill="var(--panel-2)" stroke="var(--line-2)" strokeWidth={0.6} />
          <circle cx={DISH_CX} cy={DISH_CY} r={DISH_R - 1.2} fill="url(#dishGloss)" />
          {/* Reference ring (faded) */}
          <circle cx={DISH_CX} cy={DISH_CY} r={DISH_R * 0.6} fill="none" stroke="var(--line)" strokeWidth={0.3} strokeDasharray="0.6 1.4" />
          {/* Colonies — each gets its own colour from COLONY_COLORS, with
              the synthetic "others" colony in a muted slate so it's
              visually de-emphasised. */}
          {colonies.map((c, i) => (
            <circle
              key={i}
              cx={c.x}
              cy={c.y}
              r={c.r}
              fill={c.color}
              opacity={c.isOthers ? 0.55 : 0.85}
              stroke={c.isOthers ? c.color : 'none'}
              strokeWidth={c.isOthers ? 0.6 : 0}
              strokeDasharray={c.isOthers ? '0.8 1.2' : undefined}
              style={{ cursor: 'help' }}
              {...bindHover(c)}
            />
          ))}
        </svg>
      </div>
      <div style={{ textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', letterSpacing: 1.5, marginTop: 8, textTransform: 'uppercase' }}>
        {contribCount} bid{contribCount === 1 ? '' : 's'} · live
      </div>
      {showStashes && (
        <div
          style={{
            marginTop: 6,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 4,
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: 0.5,
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          <div>
            <div style={{ color: 'var(--green)' }}>contributors</div>
            <div style={{ color: 'var(--ink)' }}>{(tokensPerPeriod * 0.70).toFixed(0)} METH</div>
            <div>70%</div>
          </div>
          <div>
            <div style={{ color: 'var(--amber)' }}>operations</div>
            <div style={{ color: 'var(--ink)' }}>{opsMeth.toFixed(0)} METH</div>
            <div>20%</div>
          </div>
          <div>
            <div style={{ color: 'var(--ctx)' }}>devs</div>
            <div style={{ color: 'var(--ink)' }}>{devMeth.toFixed(0)} METH</div>
            <div>10%</div>
          </div>
        </div>
      )}

      {hover && (
        <div
          className="mono"
          style={{
            position: 'absolute',
            left: Math.min(
              hover.cursorX + 14,
              (containerRef.current?.clientWidth ?? 220) - 220
            ),
            top: Math.max(0, hover.cursorY - 60),
            pointerEvents: 'none',
            padding: '8px 10px',
            fontSize: 11,
            lineHeight: 1.5,
            background: 'var(--panel)',
            border: '1px solid var(--line-2)',
            borderLeft: `2px solid ${hover.color}`,
            color: 'var(--ink)',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
            minWidth: 200,
          }}
        >
          <div style={{ color: hover.color, letterSpacing: 1.2, fontSize: 9, textTransform: 'uppercase', marginBottom: 4 }}>
            {hover.isOthers ? 'others (aggregated)' : 'contributor'}
          </div>
          <div style={{ color: 'var(--ink)' }}>{hover.isOthers ? 'rank 11+' : short(hover.addr)}</div>
          <div style={{ color: 'var(--dim)', fontSize: 10, marginTop: 4 }}>
            <b style={{ color: 'var(--ink)' }}>{hover.weiEth.toFixed(4)} ETH</b>{' '}
            ({hover.sharePct.toFixed(1)}% of period)
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 10 }}>
            ≈ <b style={{ color: 'var(--green)' }}>{hover.methAlloc.toFixed(2)} METH</b> at claim
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ l, v, d, tone }: { l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn' }) {
  return (
    <div className="kpi">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </div>
  );
}

/* -------------------------------------------------------------- *
 *   Add METH to MetaMask (EIP-747 wallet_watchAsset)
 * -------------------------------------------------------------- */

function AddMethToWalletButton() {
  async function onAdd() {
    if (typeof window === 'undefined' || !window.ethereum) {
      pushToast('error', 'No wallet detected', { message: 'Install or connect MetaMask first.' });
      return;
    }
    try {
      const ok = await (window.ethereum as { request: (args: { method: string; params: unknown }) => Promise<boolean> }).request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: ADDRESSES.MethaToken,
            symbol: 'METH',
            decimals: 18,
          },
        },
      });
      if (ok) {
        pushToast('success', 'METH added to MetaMask', { message: 'Token now visible in your wallet.' });
      } else {
        pushToast('info', 'Add METH cancelled', { message: 'You can re-add it any time.' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast('error', 'Add METH failed', { message: msg });
    }
  }
  return (
    <button
      className="btn"
      title="Add the METH token to MetaMask so it shows in your wallet asset list"
      onClick={onAdd}
      style={{ flex: '0 0 auto' }}
    >
      + METH
    </button>
  );
}

/* -------------------------------------------------------------- *
 *   Schedule contributions across N future periods
 * -------------------------------------------------------------- *
 *
 *   MethaCrowdsale does not have an atomic multi-period contribute,
 *   so this fires N sequential txs — one per (periodId, weiAmount)
 *   pair. The user signs one MetaMask popup per period; toasts
 *   stream the progress.
 */

type ScheduleRow = { periodOffset: string; eth: string };

export function ScheduleContributionsCard({
  account, currentPeriod, minContribEth,
}: {
  account: string | null;
  currentPeriod: number | null;
  minContribEth: number;
}) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';
  const [rows, setRows] = useState<ScheduleRow[]>([
    { periodOffset: '1', eth: '0.05' },
    { periodOffset: '2', eth: '0.05' },
  ]);

  function updateRow(i: number, patch: Partial<ScheduleRow>) {
    setRows((arr) => arr.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  }
  function addRow()    { setRows((arr) => [...arr, { periodOffset: String(arr.length + 1), eth: '0.05' }]); }
  function removeRow(i: number) { setRows((arr) => arr.filter((_, j) => j !== i)); }

  const parsed = rows.map((r) => {
    const offset = parseInt(r.periodOffset, 10);
    let wei: bigint | null = null;
    try { wei = ethers.parseEther(r.eth || '0'); } catch { wei = null; }
    return { offset, wei };
  });
  const validRows = parsed.every((p) => Number.isFinite(p.offset) && p.offset >= 0 && p.wei !== null && p.wei >= ethers.parseEther(String(minContribEth || '0')));
  const totalWei = parsed.reduce((s, p) => s + (p.wei ?? 0n), 0n);
  const totalEth = weiToEth(totalWei);

  const canSubmit = !!account && currentPeriod !== null && rows.length > 0 && validRows && !submitting;

  async function onSubmit() {
    if (!canSubmit || currentPeriod === null) return;
    pushToast('info', `Scheduling ${rows.length} contributions…`, {
      message: 'You will be asked to sign one MetaMask transaction per period.',
    });
    for (let i = 0; i < rows.length; i++) {
      const { offset, wei } = parsed[i];
      if (wei === null) continue;
      const periodId = BigInt(currentPeriod + offset);
      const result = await write({
        contract: 'crowdsale',
        fn: 'contribute',
        args: [periodId],
        options: {
          value: wei,
          pendingTitle: `[${i + 1}/${rows.length}] Contributing ${weiToEth(wei)} ETH to period ${periodId}…`,
          successTitle: `[${i + 1}/${rows.length}] Period ${periodId} funded`,
          invalidate: [['metha', 'crowdsale']],
        },
      });
      if (!result) {
        pushToast('error', `Batch aborted at ${i + 1}/${rows.length}`, {
          message: 'Previous transactions (if any) are confirmed; remaining are skipped.',
        });
        break;
      }
    }
  }

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Schedule across future periods</h3>
        <span className="src"><code>contribute(periodId) × N</code></span>
      </div>
      <div className="p-body">
        <p className="help" style={{ marginTop: 0 }}>
          Lock in METH allocations for upcoming periods. Each row fires a
          separate <span className="mono">contribute(periodId)</span> tx —
          MetaMask will pop once per row. Period offset is relative to the
          current period ({currentPeriod ?? '…'}); offset 0 means the live
          period, offset 1 the next one, etc.
        </p>
        <div className="tbl mt-14">
          <div className="th" style={{ gridTemplateColumns: '110px 1fr 110px 50px' }}>
            <span>PERIOD</span><span>OFFSET (+N)</span><span>AMOUNT</span><span></span>
          </div>
          {rows.map((r, i) => {
            const offset = parseInt(r.periodOffset, 10);
            const targetPeriod = currentPeriod !== null && Number.isFinite(offset) ? currentPeriod + offset : null;
            return (
              <div key={i} className="tr" style={{ gridTemplateColumns: '110px 1fr 110px 50px', cursor: 'default' }}>
                <span className="pid">P{targetPeriod !== null ? targetPeriod : '?'}</span>
                <span>
                  <div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                    <input
                      value={r.periodOffset}
                      onChange={(e) => updateRow(i, { periodOffset: e.target.value.replace(/[^0-9]/g, '') })}
                      placeholder="1"
                    />
                  </div>
                </span>
                <span>
                  <div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                    <input
                      value={r.eth}
                      onChange={(e) => updateRow(i, { eth: e.target.value.replace(/[^0-9.]/g, '') })}
                      placeholder="0.05"
                    />
                  </div>
                </span>
                <span>
                  <button className="btn btn-sm" onClick={() => removeRow(i)} title="Remove row">×</button>
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontFamily: 'var(--mono)', fontSize: 12 }}>
          <span className="dim">
            TOTAL <b style={{ color: 'var(--ink)', marginLeft: 6 }}>{totalEth.toFixed(4)} ETH</b>{' '}
            across {rows.length} {rows.length === 1 ? 'period' : 'periods'}
          </span>
          <button className="btn btn-sm" onClick={addRow}>+ Add row</button>
        </div>
        <button
          className={`btn btn-prim btn-block btn-lg mt-14${canSubmit ? '' : ' btn-locked'}`}
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {submitting ? `Submitting batch…` :
            !account ? 'Connect wallet to schedule' :
              rows.length === 0 ? 'Add at least one row' :
                !validRows ? `Each row needs offset ≥ 0 and amount ≥ ${minContribEth} ETH` :
                  `Submit ${rows.length} contribution${rows.length === 1 ? '' : 's'}`}
        </button>
        <p className="help mt-14" style={{ marginBottom: 0, color: 'var(--dim)' }}>
          Tip: scheduling early into future periods is useful when you expect
          ETH to be more valuable later — locking in an allocation now
          guarantees a share of the {/* tokensPerNum from parent */}
          METH minted in that period.
        </p>
      </div>
    </div>
  );
}
